import { useCallback } from 'react';
import { ApolloCache, ApolloError, Reference } from '@apollo/client';
import moment from 'moment';

import { useErpSetCandidateAttentionMutation } from 'core/graphql';

import { TERMINAL_STATUSES } from './dofinansowanieModel';

/**
 * Задача ERP #105 — отметка «повышенное внимание».
 *
 * Сотрудник дофина: «некоторые ждут, что за ними будут следить как в детском
 * саду». Отметка на кандидате — и перед стартом ближайшего живого окна его
 * активных операторов робот заводит задачу тому, кто отметил.
 */

/** Предел заметки — зеркало `NOTE_MAX` в `services/attention.py`. */
export const ATTENTION_NOTE_MAX = 300;

/**
 * Коды отказа, которые экран переводит сам. Сырой `message` сервера
 * по-русски и для польского интерфейса не годится.
 */
const KNOWN_CODES = ['invalid_input', 'not_found', 'forbidden'];

/** Ключ перевода отказа; незнакомый код → общая фраза, а не сырой текст. */
export const attentionErrorKey = (code?: string | null): string =>
  code && KNOWN_CODES.includes(code)
    ? `pages.dofinansowanie.attention.errors.${code}`
    : 'pages.dofinansowanie.attention.errors.generic';

/** Что хуку нужно знать о кандидате ДО мутации — для правки агрегатов. */
export interface IAttentionTarget {
  id: string;
  needsAttention: boolean;
  /**
   * Архивный и закрытый (терминальный статус) в счётчик доски не входят:
   * сервер считает популяцию робота — отмечен, открыт, не в архиве.
   */
  isArchived?: boolean | null;
  status?: string | null;
}

/**
 * Почему робот «повышенного внимания» этого кандидата НЕ ведёт, хотя отметка
 * стоит: в архиве или заявка закрыта (`agreement_signed` / `declined` /
 * `lost`). Зеркало `attention_population` на сервере. `null` — ведёт.
 */
export const attentionSkipReason = (candidate: {
  isArchived?: boolean | null;
  status?: string | null;
}): 'archived' | 'closed' | null => {
  if (candidate.isArchived) return 'archived';
  if (candidate.status && TERMINAL_STATUSES.includes(candidate.status)) {
    return 'closed';
  }
  return null;
};

/**
 * Окно уже идёт: старт в прошлом. Сервер отдаёт такое в `nextIntake`, только
 * когда будущих окон нет, — и задачи «перед стартом» по нему не будет.
 * Сравнение по календарной дате (старт — дата без времени), `today` — для
 * тестов.
 */
export const intakeAlreadyRunning = (
  startsOn?: string | null,
  today: moment.Moment = moment(),
): boolean =>
  Boolean(startsOn) &&
  moment(startsOn as string, 'YYYY-MM-DD').isBefore(today, 'day');

export type TAttentionOutcome =
  | { ok: true; on: boolean }
  | { ok: false; code: string };

/**
 * Аргументы поля из его ключа в кеше: `fundingCandidates({"limit":50,…})`.
 * Не разобралось — пустой объект: такой вариант просто не трогаем.
 */
const storeArgs = (
  fieldName: string,
  storeFieldName: string,
): Record<string, unknown> => {
  const raw = storeFieldName
    .slice(fieldName.length)
    .replace(/^[(:]/, '')
    .replace(/\)$/, '');
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

/**
 * 🔴 Агрегаты после смены отметки — правкой кеша, а не `refetchQueries` по
 * имени.
 *
 * Перезапрос по имени бьёт и по skip-нутым запросам (400 → «/500»), и молча
 * промахивается по неактивным. Здесь правим ровно то, что зависит от флага:
 *
 * * **Сам кандидат** обновляется нормализованным кешем из ответа мутации —
 *   карточка, строка списка, карточка доски и панель на карточке студента.
 * * **Счётчик доски** `fundingBoard.needsAttention` глобальный (фильтры доски
 *   его не сужают), поэтому ±1 во ВСЕХ вариантах. Это популяция робота:
 *   архивного и закрытого он не считает — у них число не меняется.
 * * **Отфильтрованный список** (`needsAttention: true`): снятую отметку
 *   убираем из строк сразу; поставленную вставить на её место по сортировке
 *   сервера нельзя — вариант помечается устаревшим, и активный запрос
 *   перечитывает себя СВОИМИ переменными.
 * * **«Из N»** под тем же фильтром — устаревшим: к какой колонке доски или
 *   странице относится кандидат, отсюда не видно, а ±1 не в тот вариант —
 *   неправда.
 *
 * Имена полей сверяет тест (`attentionCache.test.ts`): `cache.modify`
 * промахивается по имени молча.
 */
export const applyAttentionToCache = (
  cache: ApolloCache<unknown>,
  target: IAttentionTarget,
  isOn: boolean,
): void => {
  if (target.needsAttention === isOn) return;
  const delta = isOn ? 1 : -1;
  const id = String(target.id);

  cache.modify({
    id: 'ROOT_QUERY',
    fields: {
      fundingBoard(existing) {
        if (attentionSkipReason(target) || !existing) return existing;
        const board = existing as { needsAttention?: unknown };
        if (typeof board.needsAttention !== 'number') return existing;
        return {
          ...board,
          needsAttention: Math.max(0, board.needsAttention + delta),
        };
      },
      fundingCandidates(
        existing,
        { fieldName, storeFieldName, readField, INVALIDATE },
      ) {
        if (storeArgs(fieldName, storeFieldName).needsAttention !== true) {
          return existing;
        }
        if (isOn || !Array.isArray(existing)) return INVALIDATE;
        return (existing as Reference[]).filter(
          (ref) => String(readField('id', ref)) !== id,
        );
      },
      fundingCandidatesTotal(
        existing,
        { fieldName, storeFieldName, INVALIDATE },
      ) {
        return storeArgs(fieldName, storeFieldName).needsAttention === true
          ? INVALIDATE
          : existing;
      },
    },
  });
};

/**
 * Поставить / снять отметку. Результат — исход с КОДОМ: переводит его экран
 * (в `aria-live` рядом с переключателем), а не тост с сырым текстом сервера.
 *
 * Переключение идёт по ответу, а не оптимистично: заметка и «кто отметил»
 * приходят с сервера, и чип, мигнувший «включено» перед отказом прав, врал
 * бы ровно тем ролям, у которых кнопки быть не должно.
 */
export const useFundingAttention = () => {
  const [mutate, { loading }] = useErpSetCandidateAttentionMutation();

  const setAttention = useCallback(
    async (
      target: IAttentionTarget,
      on: boolean,
      note?: string | null,
    ): Promise<TAttentionOutcome> => {
      const trimmed = (note ?? '').trim();
      if (trimmed.length > ATTENTION_NOTE_MAX) {
        return { ok: false, code: 'invalid_input' };
      }
      try {
        const { data } = await mutate({
          // При снятии заметка сервером очищается и живёт только в журнале —
          // старую туда не пишем, чтобы «снято — <старая причина>» не
          // читалось как причина снятия.
          variables: { id: target.id, on, note: on ? trimmed || null : null },
          update: (cache, { data: response }) => {
            const payload = response?.setFundingCandidateAttention;
            if (!payload?.success || !payload.candidate) return;
            applyAttentionToCache(
              cache,
              target,
              payload.candidate.needsAttention,
            );
          },
        });
        const payload = data?.setFundingCandidateAttention;
        if (payload?.success && payload.candidate) {
          return { ok: true, on: payload.candidate.needsAttention };
        }
        const code =
          payload?.customErrors?.find((error) => error?.code)?.code ||
          'unknown';
        return { ok: false, code };
      } catch (error) {
        // Отказ прав — не `customErrors`, а GraphQL `errors`: промис
        // отклоняется, и без перехвата человек не увидел бы ничего.
        const message = (error as ApolloError)?.graphQLErrors?.[0]?.message;
        return {
          ok: false,
          code: message?.includes('permission') ? 'forbidden' : 'unknown',
        };
      }
    },
    [mutate],
  );

  return { setAttention, loading };
};
